// AUTO-GENERATED fallback mirror of /app/backend/changelog.py.
// The /whats-new page uses the live API first and falls back to this copy if
// the request fails, so members always see the full changelog.
// REGENERATE after editing the backend changelog:
//   cd /app/backend && python3 gen_changelog_js.py
export const CHANGELOG_FALLBACK = [
  {
    "version": "v1.15.0",
    "date": "November 3, 2026",
    "title": "More helpful coaching, clearer payout planning, and narrower Google access",
    "notes": [
      {
        "heading": "💬 Caddy can help with your week",
        "body": "Turn on \"Let Caddy use my relay progress\" in Settings and, when you ask about your week, Caddy can look at a current snapshot of the progress you've logged in the app, keeping money you've earned separate from money you've actually received. Progress sharing is off by default."
      },
      {
        "heading": "🔔 Caddy's gentle prompts are optional",
        "body": "A quiet, dismissible prompt can draw your attention to a target you've reached, your first completed relay, or a payment that's overdue. It never opens a chat on its own, and you can turn these prompts off separately in Settings."
      },
      {
        "heading": "🧮 Payout fees have a place in the Relay Calculator",
        "body": "If you usually pay a cut to receive or transfer your payouts, you can now enter an estimated percentage in the Relay Calculator. The estimate shows separately from taxes and lowers your projected take-home pay. It's an estimate, not a tax, and your actual fee may vary."
      },
      {
        "heading": "🔒 Google access is narrower",
        "body": "Connecting Google now asks only for per-file Drive access to your own relay sheet, instead of access to all of your spreadsheets. The updated Privacy Policy also explains Caddy's opted-in use of the progress you've logged in the app, and what happens when you send a chat message.",
        "link": {
          "to": "/privacy#google-data",
          "label": "Read the Google data section"
        }
      }
    ]
  },
  {
    "version": "v1.14.0",
    "date": "October 9, 2026",
    "title": "Instant logging, a calmer weekly check-in, and close-outs you can do any time",
    "notes": [
      {
        "heading": "⚡ Logging is now instant",
        "body": "Tap to log your earnings and the number lands straight away, no more watching a spinner while it saves. Your Google Sheet still catches every entry; it just syncs quietly in the background a moment later, so the app never makes you wait on it."
      },
      {
        "heading": "🔁 Partial payments never double-count",
        "body": "When a relay's money arrives in pieces, you can log each part as it lands and the app keeps earned and received cleanly apart. Recording the same relay more than once, or on the same day, no longer risks a doubled total or a doubled bonus; what's in your sheet always matches what you actually earned and were actually paid."
      },
      {
        "heading": "🌴 An unpaid sick day stays unpaid",
        "body": "If you mark a sick day as unpaid, it now stays exactly that. It no longer quietly draws from your PTO bank, so your accrued / drawn / remaining stays accurate and a day you chose to take without pay is never counted as paid time off."
      },
      {
        "heading": "🧾 A calmer weekly check-in",
        "body": "The relay close-out never blocks you from logging the next week, and it never turns red or scolds you for setting aside less than you planned. Zero is a perfectly valid answer, and \"Not yet\" is kept separate from $0 so you're never forced to pretend. Your savings and debt set-asides are always offered too, even if you didn't plan for them, in case real life left room for a little extra."
      },
      {
        "heading": "🔙 Close out any past week from your Scoreboard",
        "body": "You don't have to close a relay out the moment it ends. Tap any past relay on your Scoreboard to record what you set aside whenever you get to it, with your planned amounts shown as a gentle reference. A subtle gold dot marks relays that have been paid but not yet closed out, so you can see at a glance which weeks are waiting for a quick check-in."
      }
    ]
  },
  {
    "version": "v1.13.0",
    "date": "October 2, 2026",
    "title": "Pending payments, and planning tools you can open any time",
    "notes": [
      {
        "heading": "⏳ Money you've earned but haven't been paid yet",
        "body": "When you finish work but the money hasn't landed, you can now log it as pending instead of waiting. Your weekly progress counts the moment you've earned it, while taxes, PTO, savings and bonus only move once the cash actually arrives. Record a payment whenever it comes in, split across as many partial payments as it takes, and your sheet keeps earned and received cleanly apart: a relay's awaiting amount is frozen in the relay it was earned, and late money shows up in the relay you actually got paid, without ever double-counting a bonus."
      },
      {
        "heading": "🧮 Debt Strategist & Savings Splitter, open any time in the Calculator",
        "body": "The Debt Strategist (snowball vs avalanche) and the Savings Splitter (turn a goal and a deadline into a weekly number) now open straight from the Relay Calculator whenever you want to run the numbers, even before you've set a target. Use them as a scratchpad any time; nothing is locked behind setup."
      }
    ]
  },
  {
    "version": "v1.12.0",
    "date": "September 28, 2026",
    "title": "Help Guides, and a research opt-in you control",
    "notes": [
      {
        "heading": "✅ Help Guides are here",
        "body": "There's now a Help Guides section at /learn, reachable from Method in the top menu, the footer, and the top of your Resource Library. Four plain-English shelves: System Setup & Basics, Income & Payouts, Taxes & Allocations, and Work-Life Strategy, with a search box across all of them and a direct line to support when the answer isn't there yet."
      },
      {
        "heading": "🔬 Contribute to research, if you want to",
        "body": "You can now opt in to contribute anonymously to research on independent work, motivation and money. It's fully anonymized: no name, no email, no exact figures. You'll find the switch any time in Settings, under Privacy & research, and you can switch it off there too."
      },
      {
        "heading": "🔒 How we use Google data, one click from any page",
        "body": "The footer's Legal column now links straight to the Google data section of the privacy policy, alongside Privacy Policy and Terms of Service, so you can see exactly what the Google Sheet connection does and doesn't touch."
      },
      {
        "heading": "🙈 Privacy Mode now lives in your menu",
        "body": "Creator Privacy Mode, which masks every dollar amount so you can film or screen-share safely, is no longer a button floating over the screen. It's now a clean toggle inside your app menu (tap More) and in Settings under Privacy & research. Flip it on or off any time; progress bars and percentages stay live either way."
      }
    ]
  },
  {
    "version": "v1.11.0",
    "date": "September 21, 2026",
    "title": "A calmer dashboard, planning tools built into the calculator, and a sheet that agrees with the app every week",
    "notes": [
      {
        "heading": "💎 A calmer, clearer dashboard",
        "body": "The number that matters, what's left this week, is now the biggest thing on the screen, with the rest of the week arranged around it instead of competing with it. Cards have softer edges and real depth, every icon in the app now comes from one family so nothing looks borrowed, and your relay week reads as seven simple day squares with today marked. Same information, far less noise."
      },
      {
        "heading": "🧰 Pit Crew Tools, right inside your Relay Calculator",
        "body": "Three planners now open straight from the calculator instead of living on a separate page: the Debt Strategist (snowball or avalanche, side by side, so you can see which clears faster), the PTO Buffer (how much to set aside per relay to fund the weeks you want off), and the Savings Splitter (turn a goal and a deadline into a weekly number). Whatever you decide flows back into your weekly target."
      },
      {
        "heading": "🔄 Your sheet and the app now agree on every single relay",
        "body": "Your Payday Relay Google Sheet files each relay under the month it pays out in, not the month it starts in, so a relay that runs over a month boundary, say the week of 29 September paying out in October, lives on the October tab. The app had been looking in the wrong month for those weeks, which meant roughly one relay a month could read or save against the wrong block. Every relay is now matched by its payout month, so what you log and what your sheet shows always line up."
      },
      {
        "heading": "🏗️ Sheets that were being turned away now build first time",
        "body": "If your race started in the last days of a month, the final safety check couldn't find your first relay and refused a sheet that was actually perfectly built. That check now looks in the right place, so those builds go straight through."
      },
      {
        "heading": "🔗 Sign-in links that wait for you",
        "body": "Your emailed sign-in link now stays valid for a full hour, so a link opened after lunch still works, and if it has expired you get a clear \"send me a new one\" button instead of a dead end. The site header also knows who you are now: signed in it says Dashboard, signed out it says Login."
      },
      {
        "heading": "🏁 A short explainer for how the race actually works",
        "body": "New on your dashboard: a small \"How the race works\" card that names the four things people mix up, this relay, your Sprint or 52-week race, the current U.S. estimated-tax period, and your quarterly tax day. Read it once, dismiss it, and it stays gone."
      }
    ]
  },
  {
    "version": "v1.10.0",
    "date": "September 10, 2026",
    "title": "Fix any entry, any week, and a Google Sheet that builds properly wherever you live",
    "notes": [
      {
        "heading": "✏️ Change or delete any entry, in any week",
        "body": "Fixing a day used to mean a trip to the Time Off calendar, and only within the relay you were running. Now tap the day in This Week and \"Edit amount\" and \"Delete entry\" are right there. No starting over, no workarounds. And there's a new Entry History page (Entry History in the sidebar, More → Entry History on your phone, or \"Fix an earlier week\" under this week's list) where you can step back through any relay week you've raced and correct a day you missed or got wrong. Your weekly total, your streak and your sheet all catch up straight away."
      },
      {
        "heading": "🔐 The one Google box that has to stay ticked",
        "body": "Google's permission screen lets you switch off individual permissions, and if the Google Sheets one gets switched off we simply can't build your sheet, which used to end in a confusing failure with no explanation. Sheet setup now shows you a picture of that screen with the exact row highlighted before you connect, and if the permission is missing we tell you plainly and put you one tap from fixing it. You can also always get back into sheet setup now, even if an earlier attempt failed halfway."
      },
      {
        "heading": "🌍 Your sheet is built correctly wherever you are",
        "body": "If your Google account isn't set to the U.S., your new sheet was being created in your own region's format, which quietly broke the formulas and the relay calendar, so the sheet was refused before it ever reached you. Every sheet is now built in the format the template expects, so the maths, the dates and the 52-week calendar come out right whether you're in London, Lagos or Los Angeles."
      }
    ]
  },
  {
    "version": "v1.9.0",
    "date": "September 8, 2026",
    "title": "Reminders on your schedule, your membership in one place, and a sheet setup that tells you what's wrong",
    "notes": [
      {
        "heading": "⏰ Your daily nudge, at the hour that actually fits your day",
        "body": "The relay check-in used to land at 7 PM whether that suited you or not. Now you pick it: any time you like, in 15-minute steps, in your own time zone. Work nights? Set it for 11:15 PM. Log over morning coffee? Set it for 6:30 AM. There's a separate Sunday morning heads-up for the day your relay closes, which you can switch off on its own, and if you'd rather not use phone notifications at all you can have the nudge arrive by email instead. It still never fires on a day you've already logged, or on a day or week you've tagged off, sick or PTO. Your rest stays restful."
      },
      {
        "heading": "⚙️ One Settings page for reminders, billing and your account",
        "body": "Everything account-shaped now lives in one place, Settings in the sidebar on a computer, or More → Settings on your phone. You'll find your plan and next charge date, your full billing history with every receipt, a one-tap way to update the card on file, and your reminder controls. Card details are handled entirely by Stripe's secure checkout, so Payday Relay never sees or stores them. And if you ever need to cancel, you can do it yourself in two taps. Your access runs to the end of the period you've paid for, your Google Sheet stays yours forever, and there's a \"Rejoin the race\" button waiting if you change your mind."
      },
      {
        "heading": "🔧 Sheet setup now tells you exactly what went wrong, and often fixes itself",
        "body": "If building your Google Sheet ever failed, you used to get a vague \"we hit a snag\" and no way forward. Now you get the real reason and the actual next step: reconnect Google, free up Drive space, or wait a minute and try again. Better still, if Google simply hiccups mid-build, Payday Relay quietly retries for you before you ever see an error. And the reconnect banner you already know from 18 August now explains what expired, not just that something did."
      },
      {
        "heading": "📅 Your Sprint scoreboard now follows the real calendar",
        "body": "The 4-Relay Sprint sheet used to park your four relays in a generic \"Month 1\" row. It now uses the actual months your earnings happen in, and your race segments line up with the real U.S. estimated-tax periods, so what you see on the sheet matches what you'd tell an accountant. And your relay-day counter now always matches the relay you're actually in."
      }
    ]
  },
  {
    "version": "v1.8.0",
    "date": "September 3, 2026",
    "title": "Your PTO bank now writes to your sheet, Sprint mode makes sense, and the menu got out of your way",
    "notes": [
      {
        "heading": "🌴 Every paid day off now lands on your Google Sheet",
        "body": "Until now only the month's PTO-used total reached your sheet. Draw a day, or a whole week, from your PTO bank and the dollars now flow straight into the PTO USED line on your sheet, so your Scoreboard's Accrued / Drawn / Remaining is always right without you touching a cell. Edit or clear a day and the total recalculates itself. Marking time off as unpaid stays exactly that: on your record, easy on your streak, and never counted as money you drew. Sprint racers: this now works on your 4-Relay sheet too, the Time Off & PTO page is fully open to you."
      },
      {
        "heading": "🏁 Sprint mode is now intentional, not incomplete",
        "body": "If you're racing the free 4-Relay Sprint, everything you can tap actually syncs to your Sprint sheet, daily logging, your four relays, close-outs, PTO, and the journal. Anything that belongs to the full 52-week race (Bonus Bank, Quarterly Tax Days, Monthly Wrap-Up, the full Race Board, the Resource Library) is shown as a clear Season Pass preview with a short note on what it unlocks, instead of a dead end or a confusing half-feature."
      },
      {
        "heading": "🧭 A simpler menu, and a nudge before a quarterly tax day closes",
        "body": "The \"Set Allocations\" page is gone (your splits already live in the Relay Calculator), Quarterly Reset no longer clutters the More menu, the app prompts you the moment a quarterly tax day window opens, and Time Off & PTO plus Monthly Wrap-Up now sit right under the Calculator where you'd expect them. And if a quarterly tax day window is about to close and you haven't recalibrated, you'll get a heads-up in the app and by email a week out, and a last call two days before, so a quarter never locks in without you."
      }
    ]
  },
  {
    "version": "v1.7.0",
    "date": "August 23, 2026",
    "title": "Your free Sprint becomes your season, on your terms",
    "notes": [
      {
        "heading": "🏁 Lock in your first quarter before your season begins",
        "body": "When your free 4-Relay Sprint rolls into a paid season, Payday Relay now pauses for one important moment: you confirm the numbers you want to race on. Nothing is decided for you, and your full 52-week Payday Relay Google Sheet isn't built until you say \"go\", so your season starts on a plan you actually chose."
      },
      {
        "heading": "🔄 Recalibrate My Relay, rework the whole plan, not just a number",
        "body": "At that same moment you can either keep your Sprint settings or tap \"Recalibrate My Relay\" and re-run the full Relay Calculator, weekly target, days per week, savings, debt payoff, and PTO. Your fresh numbers flow straight into your season, so the four free weeks were a real test run and your season reflects what you learned."
      },
      {
        "heading": "💬 If you ever leave, one gentle question. Never a wall",
        "body": "Cancel and we won't trap you in a maze of \"are you sure?\" screens. You'll see a single, optional question, what got in the way?. That you can answer in one tap or skip entirely. Your feedback quietly helps make Payday Relay better, and your Google Sheet stays yours either way."
      }
    ]
  },
  {
    "version": "v1.6.0",
    "date": "August 18, 2026",
    "title": "Nudges that respect your days off, live sheet-sync status, and a sheet that's yours for good",
    "notes": [
      {
        "heading": "🔔 An evening nudge, so a relay never slips by",
        "body": "Turn on notifications in More → Notifications and Payday Relay taps you once at 7 PM your time: \"How much did you earn today?\" One tap logs it. If you already logged, we stay quiet. Sprint racers also get a heads-up about 24 hours before the card on file is charged, so day 28 is never a surprise."
      },
      {
        "heading": "😴 Silent on the days you've claimed as yours",
        "body": "Rest should feel like rest. If you've tagged today as a day off, sick day, or PTO, or taken the whole relay week off, the evening check-in and the Sunday close-out nudge stay quiet. No guilt pings on a day you already decided not to race."
      },
      {
        "heading": "🔄 You can now see whether your sheet is actually syncing",
        "body": "Your dashboard shows a live status line: a green \"Sheet synced 4 min ago\" when everything is flowing. If Google's permission ever expires (it happens), it turns amber, tells you when it last synced, and gives you a one-tap Reconnect Google button. No more wondering whether your sheet is quietly out of date."
      },
      {
        "heading": "🔐 Your numbers are locked to your sign-in",
        "body": "Your relay stats, targets, and streak are now readable only by your own signed-in session, nobody can look up someone else's numbers by guessing an email address. A quiet change you'll never notice, and exactly the way it should have always been."
      },
      {
        "heading": "🧾 Your sheet stays yours, even if you cancel",
        "body": "Said plainly now, everywhere it matters: your Payday Relay Google Sheet lives in your own Drive, under your own Google account. Cancel and the app simply stops filling it in for you, every relay you cleared, every set-aside, and every month you ran stays with you. No exports, no lockout, nothing deleted."
      }
    ]
  },
  {
    "version": "v1.5.0",
    "date": "August 14, 2026",
    "title": "Fix any day's earnings, retroactive edits that sync to your sheet",
    "notes": [
      {
        "heading": "✏️ Fix a day's earnings from the Time Off calendar",
        "body": "Life happens, a typo, a duplicate, or a day you decided to take off after all. This first version lives on the Time Off calendar: tap any past day of the current relay that has earnings on it and a \"Fix this day's earnings\" panel lets you set the exact amount or clear it entirely. The correction writes straight into your Google Sheet, so the app and your permanent record always match. (From 10 September the same controls sit on the day itself, and reach back into earlier relay weeks.)"
      },
      {
        "heading": "🌴 Turn a logged day into a PTO day, cleanly",
        "body": "When you tag a day as PTO, sick, or a day off and it still has earnings on it, the Caddy now asks if you want to zero those out too. One tap and the day is properly re-written as time off. No leftover numbers quietly counting toward your relay."
      },
      {
        "heading": "➕ Add to it, or replace it? Logging the same day twice no longer doubles",
        "body": "Before, logging on a day that already had a number silently ADDED to it, so retrying a fix made things worse. Now the Caddy stops and asks: add to today's total, or replace it with the new amount? Whichever you pick, the exact total lands in your sheet. No more double-counted wins."
      },
      {
        "heading": "⚡ Instant answers from the Coach",
        "body": "The most common questions, payday delay, pricing, refunds, beta codes, cancelling, now get an instant answer from the Coach the moment you ask. Trickier questions still get the full thoughtful treatment."
      }
    ]
  },
  {
    "version": "v1.4.0",
    "date": "August 11, 2026",
    "title": "Google-Sheets-only setup + quarterly tax day clarity and a smoother beta workflow",
    "notes": [
      {
        "heading": "🧭 One spreadsheet format, zero drift",
        "body": "Payday Relay now runs on Google Sheets so your formulas, formatting, and Payday Relay Mobile sync stay consistent. The Excel/XLSX download path has been retired, every new customer's Payday Relay sheet is copied straight into their own Google Drive."
      },
      {
        "heading": "🔗 Helpful nudge for people who don't have Google yet",
        "body": "The setup page now says clearly: your Payday Relay sheet lives in Google Drive, but you don't need a Gmail address to make a Google account. A small link right under the \"Connect Google\" button points to accounts.google.com/signup. You can create a Google account with any email address you already use."
      },
      {
        "heading": "🏁 Cleaner setup flow",
        "body": "No more \"I don't have Gmail\" side-door. No more Excel-workbook download. The setup wizard has one clear path: connect Google → copy your sheet → run your race. If Google connection fails, we ask you to try again rather than steering you into a spreadsheet format we can't fully support."
      },
      {
        "heading": "🗓️ quarterly tax day banner shows the full picture",
        "body": "Your Daily screen banner now spells it out: the recalibration window (e.g. Aug 20 – Sep 10), the quarterly tax deadline it's leading up to (Sep 15), and how many days until it opens. No more wondering what \"next quarterly tax day Aug 20\" means."
      },
      {
        "heading": "❓ Tap the \"?\" for a quarterly tax day cheat sheet",
        "body": "A new question-mark button on the banner opens a plain-English explainer: a quarterly tax day is a 3-week recalibration window before each quarterly tax deadline. It lists all four windows (Q1 Mar 20–Apr 10, Q2 May 20–Jun 10, Q3 Aug 20–Sep 10, Q4 Dec 15–Jan 10) with their tax due dates."
      },
      {
        "heading": "🟢 A pulsing chip on your Daily screen",
        "body": "A small chip under your welcome banner tracks your next quarterly tax day at a glance. It stays quiet indigo when you're mid-race, then pulses green the whole week your window opens and while it's live, so you can't miss it."
      },
      {
        "heading": "🏁 One more chance to say \"yes, lock it in\"",
        "body": "The Quarterly Reset flow now asks \"Ready to lock this in for September 1?\" (or whichever quarter starts next) before committing your new pace, with your Focus and any target change on the confirmation card. No accidental resets."
      },
      {
        "heading": "🔔 A morning nudge when the window opens",
        "body": "The morning your recalibration window first flips open, a one-time toast greets you on the Daily screen, so if you don't check the app that day, you're still on the calendar for it."
      },
      {
        "heading": "📊 Racer badges & Bonus bank tucked into a Stats tab",
        "body": "Your Daily screen now shows only what you came for: your goal, your entry field, and your remaining progress. Racer Badges, Bonus Bank, and PTO Bank sit behind a single \"Your stats\" tap when you want to peek, otherwise they stay out of the way."
      },
      {
        "heading": "📣 Beta Pulse moved to its own page",
        "body": "The beta feedback survey no longer competes for attention on the Daily screen. It lives on the More tab now, same questions, same 30-second nudge, just off the runway."
      },
      {
        "heading": "⛔ New \"DO NOT PURCHASE YET\" banner in the Beta Welcome email",
        "body": "The first email now leads with a bold red callout: Google's manual verification is required before purchase. Testers are told to expect a second email, \"Ready to Race\", within 24 hours. No more people buying and then getting stuck on Sheet setup for days."
      },
      {
        "heading": "🏁 Brand-new \"Ready to Race\" email",
        "body": "Sent the moment Dove verifies a beta tester on the admin dashboard. Green banner: \"Manual Verification Complete\", the same promo code re-issued as the green-light, and a direct button to the Income Map to start."
      },
      {
        "heading": "🛠️ One-tap verify from the admin dashboard",
        "body": "Every unverified beta tester now shows a \"Verify & send Ready to Race →\" button on the admin dashboard. Tap it, confirm, and the customer email fires, the row flips to \"✅ Verified\" instantly. New stat cards show verified vs awaiting counts at a glance."
      },
      {
        "heading": "🔒 Homepage now filters for patient testers",
        "body": "The beta signup ribbon now says \"Manual verification required · 24-hour setup time\" right under the pitch. Frames the delay as a feature and self-selects for the high-quality testers who read the fine print."
      }
    ]
  },
  {
    "version": "v1.3.0",
    "date": "August 10, 2026",
    "title": "Live banks from your sheet + the relay closeout that thinks like your sheet",
    "notes": [
      {
        "heading": "💰 Bonus Bank, straight from your sheet",
        "body": "The Bonus Bank now mirrors your sheet's Scoreboard: every month that finishes with a gain adds to your year total, and your Daily screen and Yearly Scoreboard show this month's gain or shortfall moving live as you log. A light month never subtracts, overshoot months quietly cover it."
      },
      {
        "heading": "🌴 A real PTO bank, accrued, drawn, remaining",
        "body": "Time Off & PTO now shows exactly what you've built up, what you've spent on paid days, and what's left, pulled live from your sheet's Scoreboard, plus this month's PTO accrued and used at a glance."
      },
      {
        "heading": "✍️ Paid days start writing into your sheet",
        "body": "Cover a PTO or sick day from your bank (or mark a whole PTO week) and the Caddy writes that month's PTO-used total into your sheet. Your sheet stays the permanent record, and your bank updates the moment it lands. At this stage it's the monthly total only, and Season sheets only."
      },
      {
        "heading": "🛡️ Race-window guardrail",
        "body": "Paid time off only counts inside your active 52-week relay. No accidental draws before your race starts or after relay 52."
      },
      {
        "heading": "🧠 A sharper Coach",
        "body": "The Coach now knows the Payday Relay Method page, the Beta Terms, the Privacy Policy, and how your new banks work, and it can always hand a conversation to Dove via the \"Send this to Dove\" button (support@paydayrelay.com)."
      },
      {
        "heading": "🧭 Tour tune-up",
        "body": "The welcome tour's Time Off and Bonus Bank steps now match the live sheet numbers, alongside the step on how off days and logged wins shift your daily target."
      },
      {
        "heading": "🧾 Planned vs actual relay closeout",
        "body": "When a relay ends, the Caddy now asks: what did you actually set aside? Tax, PTO, debt, and savings each show the planned amount next to what you really moved, over, under, or matched. No shame, just visibility. Your actuals write into your sheet's Actual column, never over a formula."
      },
      {
        "heading": "💸 The tax handoff check",
        "body": "Taxes stay a formula-driven estimate in your sheet. The closeout simply asks: did you move your tax set-aside? Planned amount, a different amount, or not yet, it's a behavior check, not tax advice."
      },
      {
        "heading": "🎯 Dynamic daily targets, straight from your sheet's logic",
        "body": "The This Week view now answers \"what do I need to make today?\" Earn more than today's target and tomorrow's shrinks; fall short and it rebalances; clear the week and it drops to $0, the same math your sheet runs, using your real quarterly work days."
      },
      {
        "heading": "🌴 Calendar-aware work days",
        "body": "Tap any day this week to log earnings or mark it a regular off day, PTO, or sick day. Off days gray out, stop counting as work days, and your remaining daily targets rebalance across the days you actually plan to work."
      },
      {
        "heading": "💰 Money lanes on the Daily screen",
        "body": "A live allocation summary shows your tax set-aside growing with every dollar you log, plus your planned debt, savings, and PTO lanes and take-home so far, the same lanes your sheet settles on payout day."
      },
      {
        "heading": "🔒 Close out before you race on",
        "body": "The Caddy now asks you to close out your last relay before logging into the next one, the handoff is the method. Full time-off weeks close automatically, and old history is never forced on you."
      },
      {
        "heading": "🏁 Cleaner boundaries + guardrails",
        "body": "Dates before your race starts or after relay 52 can't sneak into your relay math, calendar items outside the race stay app-only, and a new test suite protects the Payday Relay Method logic end to end."
      }
    ]
  },
  {
    "version": "v1.2.0",
    "date": "August 9, 2026",
    "title": "Payday Relay Mobile levels up, plus a sheet health guarantee",
    "notes": [
      {
        "heading": "📱 A real mobile app feel",
        "body": "Payday Relay Mobile now has bottom tabs on your phone, Daily, Scoreboard, Journal, Resources, and More. Your Daily screen leads with what matters: what's left this relay, logging today's earnings, your coach's tip, and this week's entries. Everything else (your sheet, allocations, time off, monthly wrap-up, membership) lives one tap away under More."
      },
      {
        "heading": "🗓️ Pick your race calendar in the app",
        "body": "Racing app-only? The Caddy now asks when your work week starts (any day, Monday through Sunday), when your first relay begins, and how many days your pay usually takes to land, so your relays match YOUR rhythm."
      },
      {
        "heading": "🩺 Sheet health check, guaranteed",
        "body": "Every new Payday Relay sheet now passes a full formula health check before you ever see it, all 12 monthly tabs, the SCOREBOARD, and the LEGEND verified error-free. If anything's off, we fix it before setup completes."
      },
      {
        "heading": "🧭 A better welcome tour",
        "body": "The welcome tour now walks you through the whole garage, logging, red/black/green, the Scoreboard, Time Off & PTO, the Resource Library, and quarterly tax days. Replay it anytime from More."
      }
    ]
  },
  {
    "version": "v1.1.0",
    "date": "August 8, 2026",
    "title": "The Strategy Bridge, plan it, then drop it straight into your race math",
    "notes": [
      {
        "heading": "🏎️ Drop into Relay Calculator (brand new)",
        "body": "Every Pit Crew tool, Debt Strategist, Savings Splitter, and PTO Buffer Planner, now has a one-tap \"Drop into Relay Calculator\" button. Work out your plan in the tool, tap once, and your numbers land pre-filled in the calculator's matching step. No retyping, no copy-paste, no \"wait, what was that number again?\""
      },
      {
        "heading": "🧭 Pit Crew shortcuts above the calculator",
        "body": "The Relay Calculator now shows quick links to all three strategy tools right above the wizard, so you can dial in your debt, savings, and time-off numbers first, then run the full calculation with confidence."
      }
    ]
  },
  {
    "version": "v1.0.0",
    "date": "August 7, 2026",
    "title": "The Beta Foundation. Everything your race runs on",
    "notes": [
      {
        "heading": "🌴 Time Off & PTO Bank (brand new)",
        "body": "Tag any day, past or future, as a day off, sick day, or PTO day from the new Time Off calendar. Sick and PTO days give you a choice: just track it (unpaid, zero guilt), or cover it from your PTO bank so the day still counts toward your relay. Mark a whole week as PTO and it shows teal on your scoreboard. Never red, and your streak stays safe. You can even schedule PTO weeks ahead of time."
      },
      {
        "heading": "🧾 Close out each relay honestly",
        "body": "After every payout, the Caddy asks what you ACTUALLY set aside for debt, savings, and PTO, because the Income Map gives the ideal, but real life gets the final say. What you log grows your PTO bank, and if your sheet is connected, both your Planned and Actual numbers land in that relay's RELAY ALLOCATIONS block automatically."
      },
      {
        "heading": "🏆 Year-End Scorecard & Race Reel",
        "body": "Finish all 52 relays and you can generate a clean, professional Year-End Scorecard with your name on it, plus a shareable Race Highlights reel showing what you earned, what you cleared, your best week, and the rest you built into the year (PTO days, sick days, and every dollar of self-paid time off)."
      },
      {
        "heading": "🔗 Magic Link login, zero memory tax",
        "body": "No passwords. Enter your email, click the secure one-time link in your inbox, and you're signed in on that device for 30 days. Bookmark paydayrelay.com/dashboard and just show up."
      },
      {
        "heading": "🏁 The Caddy. Your daily co-pilot",
        "body": "Log wins in seconds (back-date up to 30 days if you forget), watch your weekly relay progress, and let every entry sync straight into the right day cell of your Google Sheet. Streaks pause when life happens. They never reset to zero."
      },
      {
        "heading": "🔒 Goal Guardian & quarterly tax days",
        "body": "Your targets are season-locked between quarterly tax day windows (synced to US tax dates), so there's no moving the goalposts at mile 20. When a quarterly tax day opens, review the quarter and set your adjustments, taxes included."
      },
      {
        "heading": "📊 Yearly Scoreboard & Monthly Wrap-Up",
        "body": "All 52 relays on one screen, cleared, in progress, PTO weeks in teal, plus tax checkpoints, your bonus bank, racer badges, and a monthly recap you can actually feel good about."
      },
      {
        "heading": "🧰 Resource Library & Coach",
        "body": "Pit Crew tools for debt payoff timing, savings goals, and PTO buffer planning. Each able to sync your numbers to the sheet, plus an in-app coach for the days the math feels heavy."
      }
    ]
  }
];
